import { route } from 'ziggy-js';

/**
 * Map of link text patterns to route names for internal links in marketing content
 */
const LINK_TEXT_TO_ROUTE: Record<string, string> = {
    'Was kostet ein Mietkautionskonto?': 'mietkautionskonto-kosten',
    'Kosten und Gebühren bei Mietkautionskonten': 'mietkautionskonto-kosten',
    'costs and fees for rental security deposit accounts': 'mietkautionskonto-kosten',
    'Mietkautionskonto Vergleich 2026': 'mietkautionskonto-vergleich',
    'Rental Deposit Account Comparison 2026': 'mietkautionskonto-vergleich',
    'Zinsen bei Mietkautionskonten': 'zinsen-bei-mietkautionskonten',
    'wem die Zinsen bei Mietkautionskonten gehören': 'zinsen-bei-mietkautionskonten',
    'who owns the interest on rental security deposit accounts': 'zinsen-bei-mietkautionskonten',
    Volksbank: 'mietkautionskonto-volksbank',
    'Mietkautionskonto online eröffnen': 'mietkautionskonto-online-eroeffnen',
    'open a rental security deposit account online': 'mietkautionskonto-online-eroeffnen',
    'Mietkaution rechtssicher anzulegen': 'mietkaution-rechtssicher-anlegen',
    'invest a rental deposit in a legally secure way': 'mietkaution-rechtssicher-anlegen',
    'Gewerbe-Warteliste': 'gewerbe-warteliste',
    'commercial waitlist': 'gewerbe-warteliste',
};

/**
 * Default CSS classes for rendered links
 */
const DEFAULT_LINK_CLASSES = [
    'font-medium text-hs-blue underline underline-offset-4',
    'hover:text-hs-blue-dark',
    'focus-visible:text-hs-blue-dark focus-visible:outline-none',
    'focus-visible:ring-2 focus-visible:ring-hs-blue/50 focus-visible:ring-offset-2',
    'dark:text-hs-blue-light dark:hover:text-hs-blue',
    'dark:focus-visible:text-hs-blue dark:focus-visible:ring-hs-blue-light/50',
].join(' ');

/**
 * Renders text with embedded <link> markers as proper React Router links
 *
 * @param text - Text potentially containing <link>Link Text</link> markers
 * @param linkClassName - Optional custom className for the link element
 * @returns React element with properly rendered links or original text string
 *
 * @example
 * renderTextWithLinks("See our <link>Mietkautionskonto Vergleich 2026</link> for details")
 */
export function renderTextWithLinks(text: string, linkClassName = DEFAULT_LINK_CLASSES): string | React.ReactElement {
    if (!text.includes('<link>')) {
        return text;
    }

    const parts = text.split('<link>');

    // Validate that we have exactly one opening <link> tag
    if (parts.length !== 2) {
        console.warn(
            `Invalid link format in text: "${text}". Expected exactly one <link> tag. Found ${parts.length - 1} opening tags.`,
        );
        return text;
    }

    const beforeLink = parts[0];
    const linkParts = parts[1].split('</link>');

    // Validate that we have exactly one closing </link> tag
    if (linkParts.length !== 2) {
        console.warn(`Missing or invalid closing </link> tag in text: "${text}"`);
        return text;
    }

    const linkText = linkParts[0];
    const afterLink = linkParts[1];

    // Find matching route based on link text
    const routeName = LINK_TEXT_TO_ROUTE[linkText];

    if (!routeName) {
        console.warn(`No route mapping found for link text: "${linkText}". Add it to LINK_TEXT_TO_ROUTE in marketing-helpers.tsx`);
        return text; // Return original text if no mapping found
    }

    return (
        <>
            {beforeLink}
            <a href={route(routeName)} className={linkClassName}>
                {linkText}
            </a>
            {afterLink}
        </>
    );
}
